export const CAMPAIGN_AGENT_CHANGE_KIND = {
  UPDATE: "update",
  ADD: "add",
  REMOVE: "remove",
  CREATE: "create",
  CREATIVE: "creative",
};

export const CAMPAIGN_AGENT_APPLY_STATUS = {
  APPLIED: "applied",
  PARTIAL: "partial",
  FAILED: "failed",
  CREATIVE: "creative",
};

export const CAMPAIGN_AGENT_OPTIMIZATION_STATUS = {
  APPLIED: "applied",
  DISCARDED: "discarded",
};

export const CAMPAIGN_AGENT_ACTION_RESULT_MESSAGE_TYPE = "agentActionResult";

export const CAMPAIGN_AGENT_HIGHLIGHT_MS = 4500;

const HTML_TAG_REGEX = /<[^>]*>/g;

export const getCampaignAgentOptimizationElementID = (optimizationID) =>
  `campaign-agent-optimization-${optimizationID}`;

const parseJsonString = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const truncatePreviewText = ({ text, maxLength }) => {
  const trimmed = String(text).trim();
  if (maxLength != null && trimmed.length > maxLength) {
    return `${trimmed.slice(0, maxLength - 1).trimEnd()}…`;
  }
  return trimmed;
};

const isSameScalarPreview = ({ currentPreview, newPreview }) => {
  if (!currentPreview || !newPreview) {
    return false;
  }
  if (currentPreview.kind !== newPreview.kind) {
    return false;
  }
  if (currentPreview.kind === "boolean") {
    return currentPreview.value === newPreview.value;
  }
  return currentPreview.text === newPreview.text;
};

export const formatChangeValuePreview = ({ value, maxLength = 48 } = {}) => {
  const parsed = parseJsonString(value);

  if (parsed === null || parsed === undefined || parsed === "") {
    return null;
  }

  if (typeof parsed === "boolean") {
    return { kind: "boolean", value: parsed };
  }

  if (typeof parsed === "number") {
    return {
      kind: "text",
      text: truncatePreviewText({ text: String(parsed), maxLength }),
    };
  }

  if (typeof parsed === "string") {
    const text = truncatePreviewText({ text: parsed, maxLength });
    if (!text) {
      return null;
    }
    return { kind: "text", text };
  }

  if (Array.isArray(parsed)) {
    const items = parsed.filter((item) => item != null);
    if (items.length === 0) {
      return null;
    }
    if (items.length === 1) {
      return formatChangeValuePreview({ value: items[0], maxLength });
    }

    const firstPreview = formatChangeValuePreview({
      value: items[0],
      maxLength,
    });
    return {
      kind: "list",
      count: items.length,
      text: firstPreview?.kind === "text" ? firstPreview.text : null,
    };
  }

  if (typeof parsed === "object") {
    const candidate =
      parsed.name ?? parsed.value ?? parsed.label ?? parsed.title;
    if (typeof candidate === "string" || typeof candidate === "number") {
      return formatChangeValuePreview({ value: candidate, maxLength });
    }
    return null;
  }

  return null;
};

const getChangeKind = ({ operation, field, creativeFields }) => {
  if (creativeFields.includes(field)) {
    return CAMPAIGN_AGENT_CHANGE_KIND.CREATIVE;
  }

  if (operation === "update" || operation === "replaceAll") {
    return CAMPAIGN_AGENT_CHANGE_KIND.UPDATE;
  }
  if (operation === "add") {
    return CAMPAIGN_AGENT_CHANGE_KIND.ADD;
  }
  if (operation === "remove") {
    return CAMPAIGN_AGENT_CHANGE_KIND.REMOVE;
  }
  if (operation === "create") {
    return CAMPAIGN_AGENT_CHANGE_KIND.CREATE;
  }
  return CAMPAIGN_AGENT_CHANGE_KIND.UPDATE;
};

const buildSummaryItem = ({
  change,
  index,
  creativeFields,
  getCurrentValue,
  maxLength,
}) => {
  const field = change?.field;
  const kind = getChangeKind({
    operation: change?.operation,
    field,
    creativeFields,
  });
  const item = {
    key: `${index}-${field}`,
    entity: change?.entity,
    field,
    kind,
    newPreview: null,
    currentPreview: null,
    itemsCount: null,
    createdName: null,
  };

  if (kind === CAMPAIGN_AGENT_CHANGE_KIND.UPDATE) {
    const newPreview = formatChangeValuePreview({
      value: change?.value,
      maxLength,
    });
    item.newPreview = newPreview;
    if (newPreview?.kind === "list") {
      item.itemsCount = newPreview.count;
    }
    if (newPreview?.kind === "text" || newPreview?.kind === "boolean") {
      const currentPreview = formatChangeValuePreview({
        value: getCurrentValue(change),
        maxLength,
      });
      const isScalar =
        currentPreview?.kind === "text" || currentPreview?.kind === "boolean";
      if (isScalar && !isSameScalarPreview({ currentPreview, newPreview })) {
        item.currentPreview = currentPreview;
      }
    }
    return item;
  }

  if (kind === CAMPAIGN_AGENT_CHANGE_KIND.ADD) {
    const parsed = parseJsonString(change?.value);
    if (Array.isArray(parsed)) {
      item.itemsCount = parsed.length;
      item.newPreview = formatChangeValuePreview({
        value: parsed[0],
        maxLength,
      });
    } else {
      item.itemsCount = 1;
      item.newPreview = formatChangeValuePreview({ value: parsed, maxLength });
    }
    return item;
  }

  if (kind === CAMPAIGN_AGENT_CHANGE_KIND.REMOVE) {
    const itemsByValue = change?.itemsByValue;
    const itemsByID = change?.itemsByID;
    item.itemsCount = itemsByValue?.length || itemsByID?.length || 1;
    if (itemsByValue?.[0] != null) {
      item.newPreview = formatChangeValuePreview({
        value: itemsByValue[0],
        maxLength,
      });
    }
    return item;
  }

  if (kind === CAMPAIGN_AGENT_CHANGE_KIND.CREATE) {
    const parsed = parseJsonString(change?.value);
    item.createdName = parsed?.name || null;
  }

  return item;
};

export const getOptimizationChangeSummary = ({
  changes = [],
  creativeFields = [],
  getCurrentValue = () => null,
  maxItems = 3,
  maxLength = 48,
} = {}) => {
  const safeChanges = Array.isArray(changes) ? changes : [];
  const items = safeChanges.slice(0, maxItems).map((change, index) =>
    buildSummaryItem({
      change,
      index,
      creativeFields,
      getCurrentValue,
      maxLength,
    })
  );
  const totalCount = safeChanges.length;
  const hasCreativeChanges = safeChanges.some((change) =>
    creativeFields.includes(change?.field)
  );

  return {
    items,
    totalCount,
    hiddenCount: Math.max(0, totalCount - maxItems),
    hasCreativeChanges,
    isOnlyCreative:
      safeChanges.length > 0 &&
      safeChanges.every((change) => creativeFields.includes(change?.field)),
  };
};

export const getCampaignAgentApplyStatus = ({
  appliedCount = 0,
  failedCount = 0,
  isOnlyCreative = false,
} = {}) => {
  if (appliedCount === 0 && failedCount > 0) {
    return CAMPAIGN_AGENT_APPLY_STATUS.FAILED;
  }
  if (failedCount > 0) {
    return CAMPAIGN_AGENT_APPLY_STATUS.PARTIAL;
  }
  if (isOnlyCreative) {
    return CAMPAIGN_AGENT_APPLY_STATUS.CREATIVE;
  }
  return CAMPAIGN_AGENT_APPLY_STATUS.APPLIED;
};

export const countPendingOptimizations = ({ messages } = {}) => {
  const seenIDs = new Set();
  let count = 0;
  const messageList =
    messages && typeof messages === "object" ? Object.values(messages) : [];

  for (const message of messageList) {
    const optimizations = Array.isArray(message?.optimizations)
      ? message.optimizations
      : [];
    for (const optimization of optimizations) {
      if (optimization?.isApplied || optimization?.isDiscarded) {
        continue;
      }
      const id = optimization?.id;
      if (id != null && id !== "") {
        if (seenIDs.has(id)) {
          continue;
        }
        seenIDs.add(id);
      }
      count += 1;
    }
  }

  return count;
};

export const getFirstPendingOptimizationID = ({
  messages,
  sortedMessageKeys,
} = {}) => {
  const keys = Array.isArray(sortedMessageKeys) ? sortedMessageKeys : [];

  for (const key of keys) {
    const optimizations = messages?.[key]?.optimizations;
    if (!Array.isArray(optimizations)) {
      continue;
    }
    for (const optimization of optimizations) {
      if (optimization?.isApplied || optimization?.isDiscarded) {
        continue;
      }
      if (optimization?.id == null || optimization.id === "") {
        continue;
      }
      return optimization.id;
    }
  }

  return null;
};

export const getIsLongAgentAnswer = ({ text, maxLength = 420 } = {}) => {
  const plain = String(text ?? "").replace(HTML_TAG_REGEX, "");
  return plain.length > maxLength;
};

export const humanizeFieldName = (field) => {
  if (!field) {
    return "";
  }

  const spaced = String(field)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();

  if (!spaced) {
    return "";
  }

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};
